import { BehaviorSubject } from "rxjs";
import { DimensionType, IHierarchy, IHierarchyNode } from "../PA.Core/PA.Core.IF.Hierarchy";
import { IMessageProvider, IProgressProvider } from "../PA.Core/PA.Core.IF.Basic";
import { IUOMConverter, IUOMConvertOptions, IUOMPolicyOptions, UOM } from "../PA.MD.Services/PA.MD.Services.IF";
import { ICubeSourceDataSource } from "./CubeSource.IF";
import { WB_CubeSourcePerspectiveRow } from "../PA.Core/PAdbSchemaRows";

//-- Hyper Rectangle/ n-Cube
//-- An abstraction of an n-dimensional dataset that allows for
//---  Displaying data cells at arbitrary levels of each dimension hierarchy
//---  Support Two Types of Cells:
//---    1. Data Input Cells
//---    2. Readonly Calculated Metrics (Both simple aggregation & compound metrics)
//--

//-- Cell Metric Definition
//
export interface ICellMetricAggregator {
    add(cell: ICell, conversionFactor?: number): void;
    getResults(): string | number;
}

export enum ICellMetricAggregatorCalcLevel {
    Perspective = "Perspective",
    Leaf = "Leaf",
}

export enum ICellMetricPushDownMethod {
    None = "None", // For readonly metrics, no pushdown
    CopyDown = "CopyDown",
    ScaleBy = "ScaleBy", //-- Scale by previous value
    Reallocate = "Reallocate", // Fallback Even
    AllocateBy = "AllocateBy", // requires options to specify related metric, optional zero fallback
    ReallocateMetricFallback = "ReallocateMetricFallback" // requires options to specify zero fallback metric
}

export interface ICellMetricPushDownOptions {
    AllocateByMetricId?: string;
    ZeroFallbackMetricId?: string;
    [id: string]: any;
}

export enum ICellMetricNumberType {
    PositiveNumber = "PositiveNumber", // Positive Number (  1.342,  2.34, ..)
    RealNumber = "RealNumber", // Real Number (34.342, -3.4)
    Percentage = "Percentage", // Percentage(-0.12, 0, 1.34...)
    Decimal = "Decimal", // Decimal (-1.34, 0, 2.34...)
    NaturalNumber = "NaturalNumber", // Natural Number 1,2,3, ..)
    WholeNumber = "WholeNumber", // Whole Number(-1,0,2)
}

export interface ICellMetricRenderOptions {
    showThousandsSeperator?: boolean;
    decimalPlaces?: number;
    minValue?: number;
    maxValue?: number;
    numberType: ICellMetricNumberType | string;
    groupName?: string;
    [id: string]: any;
}

export interface ICellMetric {
    readonly MetricId: string;
    readonly MetricName: string;
    readonly MetricFieldName?: string;
    readonly Sort?: number;

    readonly Options: { [id: string]: any };

    readonly GroupName: string;
    readonly RenderOptions: ICellMetricRenderOptions;

    readonly SupportingMetricsIds: string[]; //-- for calculated metrics

    //--  Is this metric aggregates
    readonly bIsLeafLevelOnly: boolean;
    //--  How to aggregate to higher level
    readonly Aggregator: () => ICellMetricAggregator;
    readonly AggregatorCalcLevel: ICellMetricAggregatorCalcLevel;

    readonly bIsReadOnly: boolean;
    readonly bIsWriteBackEnabled?: boolean;
    readonly bIsHidden: boolean;

    //-- How to push down to lower level
    readonly PushDownMethod: ICellMetricPushDownMethod;
    readonly PushDownOptions: ICellMetricPushDownOptions;

    readonly RefreshMetricIdsOnRead?: string[]; //-- for calculated metrics that write back at the Leaf Level
    readonly RefreshMetricIdsOnWrite?: string[]; //-- for calculated metrics that write back on Edit
    LeafWriteOvrd?(cell: ICell, value: number | string): void;

    getMetricUOM?(options?: IUOMPolicyOptions): ICellMetricUOM | Promise<ICellMetricUOM>;
    getMetricUOMConverters?(): ICellMetricUOMConverters;
}

export interface IMetricView {
    MetricViewId: string;
    MetricViewName: string;
    Sort: number;
    MetricIds: string[];
}

//-- Cell Definition
//--    Cell has Dimension Keys and Metric Values
//
export interface ICell {
    readonly CellId: string;
    getKey(dimensionTypeId: string): string;
    getKeys(): { [dimTypeId: string]: string };

    // Loading Metric Values
    readonly bMetricValuesLoaded: boolean;
    refreshMetricValues(metricIds?: string[]): Promise<void> | void;

    // Reading Metric Values
    getCellMetricValue(metricId: string): string | number; // retrieves the calculated value stored at this level.
    getCellMetricUOM(metricId: string): ICellMetricUOM;

    //-- Needed for Leaf Metric Conversion
    getUOMConvertOptions?(MetricId: string): IUOMConvertOptions;

    // Write Metric Values
    readonly bCellEnabled: boolean;
    setCellMetricValue(MetricId: string, value: number | string): void;

    readonly OnCellChange: BehaviorSubject<{ Cell: ICell; MetricIds?: string[] }>;
}

export interface ICellMetricUOM {
    NumeratorUOM: UOM;
    DenominatorUOM: UOM;
    ConvertErrors?: { [id: string]: number };
}

export interface ICellMetricUOMConverters {
    NumeratorUOMConverters: string[];
    DenominatorUOMConverter: string[];
}

export interface IMetricUOMConversion {
    convertedId: string;
    converter: IUOMConverter;
    fromUOM: UOM;
    toUOM: UOM;
    errorCount?: number;
}

export interface ICellMetricUOMConversions {
    NumeratorConversions: IMetricUOMConversion[];
    DenominatorConversions: IMetricUOMConversion[];
}

//-- Cube Perspective Definition
//--    Perspective has Aggregate Cells

export interface ICubePerspective {
    // This can be a re-usable class implementation.
    readonly CubePerspectiveName: string;
    readonly CubePerspectiveOptions: ICubePerspectiveOptions;

    readonly LoadProgressProvider: IProgressProvider;
    readonly LoadMessageProvider: IMessageProvider;

    readonly bCubePerspectiveLoaded: boolean;
    readonly OnCubePerspectiveLoadComplete: Promise<void>;
    destroy(): void;

    // Dimension Metadata
    getDimensions(): DimensionType[];
    getDimensionHierarchy(dimensionType: DimensionType): IHierarchy;

    // Metric Metadata
    getCellMetrics(): ICellMetric[];
    getCellMetric(aMetricId: string): ICellMetric;
    getCellMetricUOM(metricId: string): ICellMetricUOM;
    getCellMetricConversions(metricId: string): ICellMetricUOMConversions;

    // Cells
    readonly bPerspectiveEnabled: boolean;
    readonly Cells: ICell[]; //-- Aggregate Perspective Cells

    // Get from the Aggr Perspective cells
    getDistinctKeys(dimensionTypes: DimensionType[]): IDimensionDistinctKeys;
    getCompositeKeys(dimensionTypes: DimensionType[]): IDimensionCompositeKeys;

    //-- pointer back to Source for any metadata
    readonly CubeSource: ICubeSource;
}

//-- Cube Source Definition
//--    Source has basic metadata (Dimensions, Hierarchies, Metrics) and methods to Retrieve Leaf Cells
//--

export interface ICubeSource {
    //-- This will be specific to the cube data source
    readonly CubeSourceName: string;

    readonly bICubeSourceLoaded: boolean;
    readonly OnICubeSourceLoadComplete: Promise<void>;

    readonly CubeSourceLoadProgressProvider: IProgressProvider;
    readonly CubeSourceLoadMessageProvider: IMessageProvider;

    createPerspective(name: string, options: ICubePerspectiveOptions): ICubePerspective;
    readonly OnCubeSourceMetricChange?: BehaviorSubject<ICubeSourceMetricChangeEvent>;

    // Cube Source Metadata
    getDimensions(): DimensionType[];
    getDimensionHierarchy(dimensionType: DimensionType): IHierarchy; // assumes one hierarchy per dimension
    getDimensionLeafLevels?(): { [dimensionType: string]: string };

    getMetrics(options?: { bIncludeWriteBack?: boolean }): ICellMetric[];
    getMetric(metricId: string): ICellMetric;

    getCellMetricBaseUOM?(metricId: string): ICellMetricUOM;
    getMetricUOMConverter?(uomConverterId: string): IUOMConverter;

    getRefreshMetricIds(aMetricId: string): string[];

    getMetricViews?(): IMetricView[];
    getProfiles?(): WB_CubeSourcePerspectiveRow[];

    // Source Leaf Cell retrieval
    // -- perspectives will utilize these methods, -- these could be over the wire, so async
    getDistinctLeafKeys(dimensionTypeIds: DimensionType[], options?: IKeyOptions): Promise<IDimensionDistinctKeys>;
    getCompositeLeafKeys(dimensionTypeIds: DimensionType[], options?: IKeyOptions): Promise<IDimensionCompositeKeys>;

    readonly OnCubeSourcePropertyChange: BehaviorSubject<ICubeSourceChangeEvent>;
}

export interface ICubeSourceChangeEvent {
    Source: ICubeSource;
    DataSource?: ICubeSourceDataSource;
    PropertyName: string;
    PropertyValue: any;
}

export interface ICubeSourceMetricChangeEvent {
    Source: ICubeSource;
    Perspective?: ICubePerspective;
    Metric: string | string[];
}

export enum IDataLocation {
    LocalLoaded = "LocalLoaded",
    RemoteAvailable = "RemoteAvailable",
}

export interface IKeyOptions {
    dataLocation?: IDataLocation;
    bIncludedLoadQueue?: boolean;
}

export interface ICubePerspectiveOptions {
    Dimensions: { [dimensionTypeId: string]: { LevelId: string } };
    DimensionFilters?: { [dimensionTypeId: string]: { FilterByNodes: IHierarchyNode[] } };
    MetricIds: string[];
    Converters?: { [converterId: string]: { ConverterId: string; UOM: UOM } };
    bEnabled?: boolean;
}

export interface ICubeSourceCellOperationUIControls {
    bLockOperation?: boolean;
    bHideOperation?: boolean;
    bLockSubOperation?: boolean;
    bLockParam1?: boolean;
    bLockSourceNode?: boolean;
    bLockTargetNodes?: boolean;
    bLockCopyMetrics?: boolean;

    bLockDimensionLevels?: boolean;
    bLockDimensionFilterScope?: boolean;

    Dimensions?: {
        [dimType: string]: {
            bLockLevel?: boolean;
            bLockFilterScope?: boolean;
        };
    };
}

export interface ICubeSourceCellOperation {
    Operation: string;
    SubOperation?: string;
    OperationParam1?: number;
    SourceMetric: string;
    TargetMetrics: string[];
    SourceNode: IHierarchyNode;
    TargetNodes: IHierarchyNode[];
    CopyMetrics: string[];
    CubePerspectiveOptions: ICubePerspectiveOptions;
    OffsetPeriods?: number;
    UI?: ICubeSourceCellOperationUIControls;
    ReallocationRule?: { Basis: string, WeekDays: string };
    MetricIds?: string[];
}

export interface IDimensionCompositeKeys {
    [key: string]: string | IDimensionCompositeKeys;
}

export interface IDimensionDistinctKeys {
    [dimTypeId: string]: string[];
}

//-- Other Definitions
//-- Rendering properties, not needed for interface, just control properties
export enum DimensionAxis {
    Rows = "Rows",
    Columns = "Columns",
    PageBy = "PageBy",
}
